import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  Typography,
  Alert,
  Box,
  TextField,
  Card,
  CardContent,
  Divider,
  Button,
  MenuItem,
  Skeleton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { DOCLINK_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { hubMedAdminDataGridSx } from "@/utils/adminTableStyles";
import { formatHubMedNoteDateTime } from "@/utils/hubMedFormatting";
import { normalizePractitionerListRow } from "@/utils/hubMedApiNormalize";
import useAlert from "@/components/feedback/useAlert";
import HubMedPractitionerDrawer from "@/components/admin/hubmed/HubMedPractitionerDrawer";

const triOptions = [
  { value: "", label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const boolIcon = (v) =>
  v ? (
    <CheckCircleIcon color="success" fontSize="small" titleAccess="Yes" />
  ) : (
    <CancelIcon sx={{ color: "text.disabled" }} fontSize="small" titleAccess="No" />
  );

const NAME_FILTER_DEBOUNCE_MS = 400;

/** HubMed GET /Practitioners — API model binder expects 0/1 for optional bools, not "true"/"false". */
const buildQueryParams = (firstName, lastName, cannabis, adhd, adhdNsw) => {
  const params = {};
  if (firstName.trim()) params.firstName = firstName.trim();
  if (lastName.trim()) params.lastName = lastName.trim();
  if (cannabis === "true" || cannabis === "false") {
    params.isCannabis = cannabis === "true" ? "1" : "0";
  }
  if (adhd === "true" || adhd === "false") {
    params.isADHD = adhd === "true" ? "1" : "0";
  }
  if (adhdNsw === "true" || adhdNsw === "false") {
    params.isADHD_NSW = adhdNsw === "true" ? "1" : "0";
  }
  return params;
};

const HubMedPractitionerSettingsPage = () => {
  const { showAlert, AlertSnackbar } = useAlert();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [debouncedFirstName, setDebouncedFirstName] = useState("");
  const [debouncedLastName, setDebouncedLastName] = useState("");
  const [cannabisFilter, setCannabisFilter] = useState("");
  const [adhdFilter, setAdhdFilter] = useState("");
  const [adhdNswFilter, setAdhdNswFilter] = useState("");

  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editDoctorId, setEditDoctorId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFirstName(firstName.trim());
      setDebouncedLastName(lastName.trim());
    }, NAME_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [firstName, lastName]);

  const loadPractitioners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildQueryParams(
        debouncedFirstName,
        debouncedLastName,
        cannabisFilter,
        adhdFilter,
        adhdNswFilter
      );
      const data = await phappsRequest(
        API_PATHS.HUBMED_ADMIN_PRACTITIONERS,
        "GET",
        null,
        params
      );
      const raw = data.practitioners ?? data.Practitioners ?? [];
      const mapped = Array.isArray(raw)
        ? raw.map(normalizePractitionerListRow).filter(Boolean)
        : [];
      mapped.sort((a, b) => {
        const last = (a.lastName || "").localeCompare(b.lastName || "", undefined, {
          sensitivity: "base",
        });
        if (last !== 0) return last;
        return (a.firstName || "").localeCompare(b.firstName || "", undefined, {
          sensitivity: "base",
        });
      });
      setPractitioners(mapped);
    } catch (err) {
      console.error(err);
      setPractitioners([]);
      setError(getAdminApiErrorMessage(err, "Failed to load practitioners"));
    } finally {
      setLoading(false);
    }
  }, [debouncedFirstName, debouncedLastName, cannabisFilter, adhdFilter, adhdNswFilter]);

  useEffect(() => {
    loadPractitioners();
  }, [loadPractitioners]);

  const runSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const data = await phappsRequest(
        API_PATHS.HUBMED_ADMIN_SYNC_PRACTITIONERS,
        "POST",
        null
      );
      setSyncResult(data);
      showAlert({
        severity: "success",
        message: data?.message || "Sync completed.",
      });
      await loadPractitioners();
    } catch (err) {
      showAlert({
        severity: "error",
        message: getAdminApiErrorMessage(err, "Sync failed"),
      });
    } finally {
      setSyncing(false);
    }
  };

  const rows = useMemo(() => practitioners, [practitioners]);

  const columns = useMemo(
    () => [
      {
        field: "firstName",
        headerName: "First name",
        flex: 1,
        minWidth: 140,
        headerAlign: "center",
        align: "center",
        renderCell: (p) => p.value || "—",
      },
      {
        field: "lastName",
        headerName: "Last name",
        flex: 1,
        minWidth: 140,
        headerAlign: "center",
        align: "center",
        renderCell: (p) => p.value || "—",
      },
      {
        field: "isCannabis",
        headerName: "Cannabis",
        width: 100,
        headerAlign: "center",
        align: "center",
        sortable: false,
        cellClassName: "hubmed-practitioner-bool-cell",
        renderCell: (p) => boolIcon(!!p.value),
      },
      {
        field: "isADHD",
        headerName: "ADHD",
        width: 90,
        headerAlign: "center",
        align: "center",
        sortable: false,
        cellClassName: "hubmed-practitioner-bool-cell",
        renderCell: (p) => boolIcon(!!p.value),
      },
      {
        field: "isADHD_NSW",
        headerName: "ADHD NSW",
        width: 110,
        headerAlign: "center",
        align: "center",
        sortable: false,
        cellClassName: "hubmed-practitioner-bool-cell",
        renderCell: (p) => boolIcon(!!p.value),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (p) => (
          <Button
            size="small"
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              setEditDoctorId(p.row.doctorId);
              setDrawerOpen(true);
            }}
            sx={{
              textTransform: "none",
              backgroundColor: DOCLINK_COLORS.MID_PURPLE,
              minWidth: 64,
            }}
          >
            Edit
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <div className="h-full min-h-0 flex flex-col bg-white overflow-x-hidden">
      <div className="flex-1 min-h-0 flex flex-col p-2 sm:p-4 space-y-3 w-full">
        {AlertSnackbar}

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}
            >
              Practitioner settings
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            <Button
              variant="contained"
              disabled={syncing}
              onClick={runSync}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: DOCLINK_COLORS.MID_PURPLE,
              }}
            >
              {syncing ? "Syncing…" : "Sync HubMed doctors to Halo"}
            </Button>
            <Button
              component={NavLink}
              to="/hubmed-admin/overview"
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                borderColor: DOCLINK_COLORS.DARK_PURPLE,
                color: DOCLINK_COLORS.DARK_PURPLE,
                fontWeight: 600,
              }}
            >
              Back to overview
            </Button>
          </Box>
        </Box>

        {syncResult ? (
          <Alert severity="success" onClose={() => setSyncResult(null)} sx={{ maxWidth: 900 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Last sync
            </Typography>
            <Box
              component="ul"
              sx={{ m: 0, pl: 2, typography: "body2", columns: { xs: 1, sm: 2 }, columnGap: 3 }}
            >
              {(syncResult.message ?? syncResult.Message) ? (
                <li>{String(syncResult.message ?? syncResult.Message)}</li>
              ) : null}
              {(syncResult.inserted ?? syncResult.Inserted) != null ? (
                <li>Inserted: {syncResult.inserted ?? syncResult.Inserted}</li>
              ) : null}
              {(syncResult.updated ?? syncResult.Updated) != null ? (
                <li>Updated: {syncResult.updated ?? syncResult.Updated}</li>
              ) : null}
              {(syncResult.skipped ?? syncResult.Skipped) != null ? (
                <li>Skipped: {syncResult.skipped ?? syncResult.Skipped}</li>
              ) : null}
              {(syncResult.markedInactive ?? syncResult.MarkedInactive) != null ? (
                <li>
                  Marked inactive:{" "}
                  {syncResult.markedInactive ?? syncResult.MarkedInactive}
                </li>
              ) : null}
              {(syncResult.totalFromApi ?? syncResult.TotalFromApi) != null ? (
                <li>
                  Total from API: {syncResult.totalFromApi ?? syncResult.TotalFromApi}
                </li>
              ) : null}
              {(syncResult.activePractitionerIdCount ??
                syncResult.ActivePractitionerIdCount) != null ? (
                <li>
                  Active practitioner IDs:{" "}
                  {syncResult.activePractitionerIdCount ??
                    syncResult.ActivePractitionerIdCount}
                </li>
              ) : null}
              {(syncResult.upserted ?? syncResult.Upserted) != null ? (
                <li>Upserted: {syncResult.upserted ?? syncResult.Upserted}</li>
              ) : null}
              {(syncResult.syncedAt ?? syncResult.SyncedAt) ? (
                <li>
                  Synced at:{" "}
                  {formatHubMedNoteDateTime(syncResult.syncedAt ?? syncResult.SyncedAt)}
                </li>
              ) : null}
              {(syncResult.durationMs ?? syncResult.DurationMs) != null ? (
                <li>Duration: {syncResult.durationMs ?? syncResult.DurationMs} ms</li>
              ) : null}
            </Box>
          </Alert>
        ) : null}

        {error ? <Alert severity="warning">{error}</Alert> : null}

        <Card
          variant="outlined"
          sx={{
            borderColor: DOCLINK_COLORS.DARK_PURPLE,
            borderRadius: 2,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent sx={{ p: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                label="First name contains"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                sx={{ minWidth: 180, flex: "1 1 160px" }}
              />
              <TextField
                size="small"
                label="Last name contains"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                sx={{ minWidth: 180, flex: "1 1 160px" }}
              />
              <TextField
                select
                size="small"
                label="Cannabis"
                value={cannabisFilter}
                onChange={(e) => setCannabisFilter(e.target.value)}
                sx={{ minWidth: 120 }}
              >
                {triOptions.map((o) => (
                  <MenuItem key={`c-${o.value}`} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="ADHD"
                value={adhdFilter}
                onChange={(e) => setAdhdFilter(e.target.value)}
                sx={{ minWidth: 120 }}
              >
                {triOptions.map((o) => (
                  <MenuItem key={`a-${o.value}`} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="ADHD NSW"
                value={adhdNswFilter}
                onChange={(e) => setAdhdNswFilter(e.target.value)}
                sx={{ minWidth: 120 }}
              >
                {triOptions.map((o) => (
                  <MenuItem key={`n-${o.value}`} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Divider />
            {loading ? (
              <Box sx={{ flex: 1, minHeight: 240, p: 2 }}>
                <Skeleton variant="rounded" height="100%" sx={{ minHeight: 360 }} />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  width: "100%",
                  px: 1,
                  pb: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <DataGrid
                  rows={rows}
                  columns={columns}
                  loading={loading}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 100 } },
                  }}
                  disableRowSelectionOnClick
                  sx={{
                    ...hubMedAdminDataGridSx,
                    height: "100%",
                    minHeight: 0,
                    "& .MuiDataGrid-cell.hubmed-practitioner-bool-cell": {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </div>

      <HubMedPractitionerDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditDoctorId(null);
        }}
        doctorId={editDoctorId}
        showAlert={showAlert}
        onSaved={(updated) => {
          setPractitioners((prev) =>
            prev.map((p) => (p.doctorId === updated.doctorId ? { ...p, ...updated } : p))
          );
        }}
      />
    </div>
  );
};

export default HubMedPractitionerSettingsPage;
